import React, { useState } from 'react';
import { familleTypeArticleService } from '../../../../services/familleTypeArticleService';
import { useTheme } from '../../../../contexts/ThemeContext';

const DeleteFamilleTypeArticleModal = ({ isOpen, familleTypeArticle, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const handleDelete = async () => {
    if (confirmText !== familleTypeArticle?.designation) {
      setError('Le texte de confirmation ne correspond pas à la désignation de la famille');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await familleTypeArticleService.deleteFamilleTypeArticle(familleTypeArticle.id);
      
      if (result.success) {
        onSuccess();
        resetForm();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Erreur lors de la suppression de la famille de type d\'article');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setConfirmText('');
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getFamilleColor = (designation) => {
    // Remove color, always return neutral in dark mode
    return isDarkMode ? 'text-gray-200' : 'text-gray-600';
  };

  const getFamilleIcon = (designation) => {
    const iconMap = {
      'RIA': 'fas fa-fire-extinguisher',
      'Extincteur': 'fas fa-spray-can',
      'PIA': 'fas fa-shield-alt',
      'AUTO': 'fas fa-cog',
      'UMM': 'fas fa-tools'
    };
    return iconMap[designation] || 'fas fa-tag';
  };

  if (!isOpen || !familleTypeArticle) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-red-600 to-red-700'} px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                <i className="fas fa-trash text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Supprimer la Famille de Type d'Article</h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-red-100'}`}>Cette action est irréversible</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
            >
              <i className="fas fa-times text-lg"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(95vh-80px)]">
          <div className="p-6">
            {/* Error Message */}
            {error && (
              <div className={`mb-6 p-4 rounded-lg border-l-4 flex items-center ${
                isDarkMode
                  ? 'bg-gray-900 border-gray-800 text-gray-200'
                  : 'bg-red-50 border-red-500 text-red-700'
              }`}>
                <i className={`fas fa-exclamation-circle mr-2 ${isDarkMode ? 'text-gray-400' : 'text-red-500'}`}></i>
                <span>{error}</span>
              </div>
            )}

            {/* Warning */}
            <div className={`mb-6 rounded-xl p-6 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-gray-800' : 'bg-red-100'}`}>
                    <i className="fas fa-exclamation-triangle text-gray-400"></i>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-red-900'}`}>
                    Attention : Suppression définitive
                  </h3>
                  <div className={`${isDarkMode ? 'text-gray-300' : 'text-red-700'}`}>
                    <p>Vous êtes sur le point de supprimer définitivement cette famille de type d'article :</p>
                    <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-red-200'} rounded-lg p-4 border`}>
                      <div className="flex items-center">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${isDarkMode ? 'bg-gray-700' : 'bg-red-50'}`}>
                          <i className={`${getFamilleIcon(familleTypeArticle.designation)} ${getFamilleColor(familleTypeArticle.designation)} text-lg`}></i>
                        </div>
                        <div>
                          <h4 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{familleTypeArticle.designation}</h4>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>ID: {familleTypeArticle.id}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Consequences */}
            <div className={`mb-6 rounded-xl p-6 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-gray-800' : 'bg-amber-100'}`}>
                    <i className="fas fa-info-circle text-gray-400"></i>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-amber-900'}`}>
                    Conséquences de cette suppression
                  </h3>
                  <ul className={`${isDarkMode ? 'text-gray-300' : 'text-amber-800'} space-y-2`}>
                    <li className="flex items-start">
                      <i className={`fas fa-chevron-right mt-1 mr-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-amber-600'}`}></i>
                      <span>La famille de type d'article sera définitivement supprimée</span>
                    </li>
                    <li className="flex items-start">
                      <i className={`fas fa-chevron-right mt-1 mr-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-amber-600'}`}></i>
                      <span>Tous les types d'articles liés à cette famille pourraient être affectés</span>
                    </li>
                    <li className="flex items-start">
                      <i className={`fas fa-chevron-right mt-1 mr-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-amber-600'}`}></i>
                      <span>Cette action ne peut pas être annulée</span>
                    </li>
                    <li className="flex items-start">
                      <i className={`fas fa-chevron-right mt-1 mr-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-amber-600'}`}></i>
                      <span>Les historiques et références seront perdus</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Confirmation Input */}
            <div className={`mb-6 rounded-xl p-6 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Confirmation de suppression
              </h3>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
                Pour confirmer la suppression, veuillez taper exactement la désignation de la famille :
              </p>
              <div className={`rounded-lg p-3 mb-4 ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-100 border-gray-200'} border`}>
                <code className={`text-lg font-mono font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {familleTypeArticle.designation}
                </code>
              </div>
              <div className="space-y-2">
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Tapez la désignation pour confirmer
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors font-mono ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-red-500'}`}
                  placeholder={familleTypeArticle.designation}
                />
              </div>
              {confirmText && confirmText !== familleTypeArticle.designation && (
                <p className="text-red-600 text-sm mt-2">
                  <i className="fas fa-times-circle mr-1"></i>
                  Le texte ne correspond pas
                </p>
              )}
              {confirmText === familleTypeArticle.designation && (
                <p className="text-green-600 text-sm mt-2">
                  <i className="fas fa-check-circle mr-1"></i>
                  Confirmation valide
                </p>
              )}
            </div>

            {/* Alternative Actions */}
            <div className={`mb-6 rounded-xl p-6 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'}`}>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-gray-800' : 'bg-blue-100'}`}>
                    <i className={`fas fa-lightbulb ${isDarkMode ? 'text-gray-400' : 'text-blue-600'}`}></i>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-blue-900'}`}>
                    Alternatives à la suppression
                  </h3>
                  <div className={`${isDarkMode ? 'text-gray-300' : 'text-blue-800'} space-y-2`}>
                    <p>Avant de supprimer définitivement, considérez ces options :</p>
                    <ul className="space-y-1 ml-4">
                      <li>• Modifier la désignation si elle n'est plus appropriée</li>
                      <li>• Archiver la famille pour un usage futur</li>
                      <li>• Vérifier s'il existe des dépendances</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={`flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                type="button"
                onClick={handleClose}
                className={`px-6 py-3 rounded-lg transition-colors font-medium ${isDarkMode ? 'text-gray-300 bg-gray-700 hover:bg-gray-600' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'}`}
              >
                <i className="fas fa-times mr-2"></i>
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={loading || confirmText !== familleTypeArticle.designation}
                className={`px-6 py-3 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                  loading || confirmText !== familleTypeArticle.designation
                    ? 'bg-gray-400 cursor-not-allowed text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Suppression...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash mr-2"></i>
                    Supprimer définitivement
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteFamilleTypeArticleModal;